import { useMemo } from 'react';
import { format, isToday, isYesterday, isThisYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { CreateDocumentAction, InboxEmail, InboxEmailStatus } from '../../types/inbox.types';
import { cleanEmailBodyText } from '../../utils/cleanEmailContent';

type Props = {
  emails: InboxEmail[];
  currentTab: InboxEmailStatus;
  searchTerm: string;
};

export type InboxListRow = {
  id: string;
  sender: string;
  subject: string;
  preview: string;
  date: string;
  dateTime: string;
  unread: boolean;
  quoteLabel: string | null;
  hasEvent: boolean;
  attachmentsCount: number;
};

type EmptyStateContent = {
  title: string;
  subtitle: string;
};

const PREVIEW_MAX_LENGTH = 200;

export const TAB_LABELS: Record<InboxEmailStatus, string> = {
  pending: 'À traiter',
  processed: 'Traités',
  ignored: 'Ignorés',
};

const LIST_LABELS: Record<InboxEmailStatus, string> = {
  pending: 'E-mails à traiter',
  processed: 'E-mails traités',
  ignored: 'E-mails ignorés',
};

const EMPTY_STATES: Record<InboxEmailStatus, EmptyStateContent> = {
  pending: {
    title: 'Votre boîte est à jour',
    subtitle: 'Aucun nouvel e-mail en attente de traitement.',
  },
  processed: {
    title: 'Aucun e-mail traité',
    subtitle: 'Les e-mails que vous classez apparaîtront ici.',
  },
  ignored: {
    title: 'Aucun e-mail ignoré',
    subtitle: 'Les e-mails que vous écartez apparaîtront ici.',
  },
};

const amountFormatter = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export function formatEmailDate(dateString: string): string {
  const date = new Date(dateString);
  if (isToday(date)) {
    return format(date, 'HH:mm');
  }
  if (isYesterday(date)) {
    return 'Hier';
  }
  if (isThisYear(date)) {
    return format(date, 'd MMM', { locale: fr });
  }
  return format(date, 'dd/MM/yyyy');
}

export function toInboxListRow(email: InboxEmail): InboxListRow {
  const quoteAction = email.parsedActions?.find(
    (action): action is CreateDocumentAction => action.type === 'CREATE_DOCUMENT' && Boolean(action.isQuote)
  );

  let quoteLabel: string | null = null;
  if (quoteAction) {
    quoteLabel = quoteAction.suggestedAmount
      ? `Devis · ${amountFormatter.format(quoteAction.suggestedAmount)}`
      : 'Devis';
  }

  return {
    id: email.id,
    sender: email.from_name || email.from_email,
    subject: email.subject || '(Sans objet)',
    preview: cleanEmailBodyText(email.body_text).slice(0, PREVIEW_MAX_LENGTH),
    date: formatEmailDate(email.received_at),
    dateTime: email.received_at,
    unread: email.status === 'pending',
    quoteLabel,
    hasEvent: Boolean(email.parsedActions?.some((action) => action.type === 'CREATE_EVENT')),
    attachmentsCount: email.attachments?.length ?? 0,
  };
}

export function useInboxList(props: Props) {
  // Search runs server-side (see useGetInboxEmails): every loaded email already matches.
  const rows = useMemo(() => props.emails.map(toInboxListRow), [props.emails]);

  const trimmedSearch = props.searchTerm.trim();
  const emptyState: EmptyStateContent = trimmedSearch
    ? {
        title: 'Aucun résultat',
        subtitle: `Aucun e-mail ne correspond à « ${trimmedSearch} ».`,
      }
    : EMPTY_STATES[props.currentTab];

  return {
    rows,
    emptyState,
    listLabel: LIST_LABELS[props.currentTab],
  };
}
